import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { requestSpeakingModelAnswer, type SpeakingModelAnswerInfo } from '../api';
import { analysisLanguage } from '../language';

export interface SpeakingModelAnswerProps {
  attemptId: string;
  questionId: string;
  /** The prompt this is for: `<part>-<prompt>`, or a live follow-up's `<part>-<prompt>-f1`. */
  clipKey: string;
  /** The one already written for this prompt, if any. */
  saved: SpeakingModelAnswerInfo | null;
  className?: string;
}

/** The i18n key for what went wrong, from the API's error code. Anything unrecognised is a plain failure. */
function failureKey(error: unknown): string {
  const code = (error as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code;
  if (code === 'RESULTS_HIDDEN') return 'speaking_model_answer.unavailable';
  return 'speaking_model_answer.failed';
}

/**
 * "AI namuna", under a Speaking prompt: pressed by the candidate, never automatic. It shows a short analysis in their
 * language and a stronger answer in English: their own answer said one or two CEFR levels higher, or, when they gave
 * no usable answer, a sample answer to the question. A sample to learn from, not the correct answer, and it is not
 * scored; the note says so.
 */
export function SpeakingModelAnswer({ attemptId, questionId, clipKey, saved, className }: SpeakingModelAnswerProps) {
  const { t, i18n } = useTranslation();
  const language = analysisLanguage(i18n.language);
  const queryClient = useQueryClient();
  const key = ['speaking-model-answer', attemptId, questionId, clipKey];

  // Not fetched here: the text arrives from the result payload or from the mutation below, and is kept under
  // this key so moving to another question and back does not ask again.
  const { data: modelAnswer } = useQuery<SpeakingModelAnswerInfo | null>({
    queryKey: key,
    queryFn: () => Promise.resolve(saved),
    initialData: saved,
    enabled: false,
    staleTime: Infinity,
  });

  // A ref, not `isPending`: two clicks in the same tick both see the old render.
  const inFlight = useRef(false);
  const create = useMutation({
    mutationFn: () => requestSpeakingModelAnswer(attemptId, questionId, clipKey, language),
    onSuccess: (result) => queryClient.setQueryData(key, result.modelAnswer),
    onSettled: () => {
      inFlight.current = false;
    },
  });

  if (modelAnswer) {
    const improved = modelAnswer.mode === 'IMPROVED';
    return (
      <div
        className={cn(
          'mt-3 rounded-xl border border-primary-200 bg-primary-50 p-3.5 dark:border-primary/30 dark:bg-primary/10',
          className,
        )}
      >
        <div className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-foreground">
          {/* Volt is a fill: the mark sits on a volt chip in ink, never as volt-on-tint. */}
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
          </span>
          <span>{t('speaking_model_answer.title')}</span>
          <span className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-background px-2 py-0.5 font-mono text-[11px] dark:border-primary/30">
            {improved && modelAnswer.currentLevel ? (
              <>
                {modelAnswer.currentLevel}
                <ArrowRight className="h-3 w-3" aria-hidden />
              </>
            ) : null}
            {modelAnswer.targetLevel}
          </span>
        </div>

        <div className="mt-2.5 border-t border-primary-200 pt-2.5 dark:border-primary/20">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('speaking_model_answer.analysis_title')}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{modelAnswer.analysis}</p>
        </div>

        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('speaking_model_answer.answer_title')}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{modelAnswer.text}</p>
        </div>

        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
          {t(improved ? 'speaking_model_answer.note_improved' : 'speaking_model_answer.note_sample')}
        </p>
      </div>
    );
  }

  return (
    <div className={cn('mt-3', className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={create.isPending}
        onClick={() => {
          if (inFlight.current) return;
          inFlight.current = true;
          create.mutate();
        }}
      >
        {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : <Sparkles className="mr-2 h-4 w-4" aria-hidden />}
        {create.isPending ? t('speaking_model_answer.working') : t('speaking_model_answer.button')}
      </Button>
      {create.isError ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {t(failureKey(create.error))}
        </p>
      ) : null}
    </div>
  );
}
